import React from 'react';

interface CustomerDashboardData {
  customer: {
    name: string;
    email: string;
    stats: {
      totalOrders: number;
      totalTreesOrdered: number;
      treesPlanted: number;
      totalAmountSpent: number;
    };
  };
  orders: {
    total: number;
    planted: number;
    inProgress: number;
    list: any[];
  };
  recentUpdates: any[];
}

interface CustomerDashboardViewModalProps {
  dashboardData: CustomerDashboardData;
  onClose: () => void;
}

export const CustomerDashboardViewModal: React.FC<CustomerDashboardViewModalProps> = ({
  dashboardData,
  onClose
}) => {
  const { customer, orders, recentUpdates } = dashboardData;

  const getStatusColor = (status: string) => {
    const colors = {
      'pending': { background: '#fff3cd', color: '#856404' },
      'assigned': { background: '#d4edda', color: '#155724' },
      'planted': { background: '#d1ecf1', color: '#0c5460' },
      'completed': { background: '#d1e7dd', color: '#0a3622' }
    };
    return colors[status as keyof typeof colors] || { background: '#e9ecef', color: '#495057' };
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          borderRadius: '15px 15px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.3rem' }}>
              Customer Dashboard View
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
              {customer.name} ({customer.email})
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: 'white',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Stats Overview */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1.1rem',
              marginBottom: '1rem',
              color: '#333',
              borderBottom: '2px solid #228B22',
              paddingBottom: '0.5rem'
            }}>
              Impact Overview
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                color: 'white',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(34, 139, 34, 0.3)'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  {customer.stats.treesPlanted}
                </div>
                <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Trees Planted</div>
              </div>

              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center',
                border: '2px solid #228B22'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#228B22', marginBottom: '0.5rem' }}>
                  {orders.inProgress}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>In Progress</div>
              </div>

              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center',
                border: '2px solid #228B22'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#228B22', marginBottom: '0.5rem' }}>
                  {customer.stats.totalOrders}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Total Orders</div>
              </div>

              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center',
                border: '2px solid #228B22'
              }}>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#228B22', marginBottom: '0.5rem' }}>
                  ${(customer.stats.totalAmountSpent / 100).toFixed(2)}
                </div>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>Lifetime Value</div>
              </div>
            </div>
          </div>

          {/* Recent Updates */}
          {recentUpdates.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.1rem',
                marginBottom: '1rem',
                color: '#333',
                borderBottom: '2px solid #228B22',
                paddingBottom: '0.5rem'
              }}>
                Recent Photo Updates
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1rem'
              }}>
                {recentUpdates.map((order, index) => (
                  <div
                    key={index}
                    style={{
                      border: '1px solid #eee',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      background: '#fafafa'
                    }}
                  >
                    {/* Photo preview */}
                    {order.photos && order.photos.length > 0 && (
                      <div style={{
                        position: 'relative',
                        paddingTop: '75%',
                        background: '#e9ecef',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={`/api/photos/${typeof order.photos[0] === 'string' ? order.photos[0] : order.photos[0].filename}`}
                          alt="Tree"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        {order.photos.length > 1 && (
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '5px',
                            fontSize: '0.8rem'
                          }}>
                            +{order.photos.length - 1} more
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ padding: '1rem' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                      }}>
                        <span style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                          {order.treesOrdered || order.trees} trees
                        </span>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '15px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          ...getStatusColor(order.status)
                        }}>
                          {order.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </div>
                      {order.assignedWorker && (
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.3rem' }}>
                          Worker: {order.assignedWorker.name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Orders */}
          <div style={{ marginBottom: '1rem' }}>
            <h3 style={{
              fontSize: '1.1rem',
              marginBottom: '1rem',
              color: '#333',
              borderBottom: '2px solid #228B22',
              paddingBottom: '0.5rem'
            }}>
              All Orders ({orders.total})
            </h3>
            {orders.list.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                No orders found
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: 'white'
                }}>
                  <thead style={{
                    background: '#f8f9fa',
                    position: 'sticky',
                    top: 0,
                    zIndex: 5
                  }}>
                    <tr>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #228B22' }}>Order ID</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #228B22' }}>Date</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #228B22' }}>Trees</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #228B22' }}>Amount</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #228B22' }}>Status</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '2px solid #228B22' }}>Worker</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '2px solid #228B22' }}>Photos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.list.map((order) => (
                      <tr key={order._id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                          #{order._id.slice(-6)}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                          {order.treesOrdered || order.trees}
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                          {order.currency || 'USD'} {((order.totalAmount || order.amount || 0) / 100).toFixed(2)}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '15px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            ...getStatusColor(order.status)
                          }}>
                            {order.status}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.9rem' }}>
                          {order.assignedWorker ? order.assignedWorker.name : '-'}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {order.photos && order.photos.length > 0 ? (
                            <span style={{
                              background: '#228B22',
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: '10px',
                              fontSize: '0.8rem'
                            }}>
                              {order.photos.length}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontSize: '0.8rem' }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Close Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingTop: '1rem',
            borderTop: '1px solid #eee',
            position: 'sticky',
            bottom: 0,
            background: 'white',
            zIndex: 10
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
